/**
 * Auth API - Olympus Betreuungsverwaltung
 * Authentifizierung, Benutzerverwaltung und Benachrichtigungen
 */

// Same-Origin-Helper (P0-Fix 26.09.2026, systemisch erweitert am 26.09.2026):
// ALLE Olympus-internen Aufrufe (Auth wie Benutzerverwaltung/Benachrichtigungen)
// MUESSEN relativ zum aktuellen Origin bleiben, damit derselbe Produktions-Build
// sowohl ueber die Domain als auch ueber andere Zugriffswege (z.B. VPN/
// Tailscale-IP) funktioniert, ohne dass der Browser einen Cross-Origin-
// Preflight sendet, den die bewusst strikte Backend-CORS-Whitelist ablehnt
// (DSGVO Art. 32). In lokaler Entwicklung (Dev-Server auf eigenem Port ohne
// Proxy) bleibt REACT_APP_BACKEND_URL nutzbar.
const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');
const SAME_ORIGIN_BASE = BACKEND_URL;

class AuthAPI {
  constructor() {
    this.token = localStorage.getItem('olympus_token');
    this.user = JSON.parse(localStorage.getItem('olympus_user') || 'null');
  }

  // ==================== Token Management ====================
  
  setAuth(token, user) {
    this.token = token;
    this.user = user;
    localStorage.setItem('olympus_token', token);
    localStorage.setItem('olympus_user', JSON.stringify(user));
  }

  clearAuth() {
    this.token = null;
    this.user = null;
    localStorage.removeItem('olympus_token');
    localStorage.removeItem('olympus_user');
  }

  getAuthHeaders() {
    return this.token ? { 'Authorization': `Bearer ${this.token}` } : {};
  }

  isAuthenticated() {
    return !!this.token;
  }

  getCurrentUser() {
    return this.user;
  }

  /**
   * Zentraler fetch-Wrapper: Bei 401 wird einmalig ein Token-Refresh versucht.
   * Verhindert dass das UI bei langen Operationen (Re-Crawl) die Verbindung verliert.
   */
  async apiFetch(url, options = {}) {
    const headers = { ...this.getAuthHeaders(), ...(options.headers || {}) };
    let response = await fetch(url, { ...options, headers });

    if (response.status === 401 && this.token && !options._retried) {
      // Token-Refresh versuchen
      try {
        const refreshResp = await fetch(`${SAME_ORIGIN_BASE}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${this.token}` }
        });
        if (refreshResp.ok) {
          const data = await refreshResp.json();
          this.token = data.access_token;
          localStorage.setItem('olympus_token', data.access_token);
          // Request mit neuem Token wiederholen
          const retryHeaders = { ...options.headers, 'Authorization': `Bearer ${this.token}` };
          response = await fetch(url, { ...options, headers: retryHeaders, _retried: true });
        } else {
          // Refresh fehlgeschlagen → Session wirklich abgelaufen
          this.clearAuth();
          window.location.reload();
        }
      } catch {
        this.clearAuth();
        window.location.reload();
      }
    }

    return response;
  }

  hasRole(roles) {
    if (!this.user) return false;
    const allowedRoles = Array.isArray(roles) ? roles : [roles];
    return allowedRoles.includes(this.user.rolle);
  }

  isAdmin() {
    return this.hasRole('admin');
  }

  // ==================== Auth Endpoints ====================

  async login(email, passwort) {
    // XMLHttpRequest statt fetch – umgeht Service Worker und fetch-Interceptoren komplett
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${SAME_ORIGIN_BASE}/api/auth/login`);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 400) {
            reject(new Error(data.detail || 'Login fehlgeschlagen'));
            return;
          }
          if (data.requires_2fa) {
            resolve(data);
            return;
          }
          this.setAuth(data.access_token, data.benutzer);
          resolve(data);
        } catch {
          reject(new Error(`Server-Fehler (${xhr.status}): ${xhr.responseText?.substring(0, 100)}`));
        }
      };
      xhr.onerror = () => reject(new Error('Netzwerkfehler - Server nicht erreichbar'));
      xhr.send(JSON.stringify({ email, passwort }));
    });
  }

  async logout() {
    try {
      await fetch(`${SAME_ORIGIN_BASE}/api/auth/logout`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });
    } catch (e) {
      // Ignore errors on logout
    }
    this.clearAuth();
  }

  async getMe() {
    const response = await fetch(`${SAME_ORIGIN_BASE}/api/auth/me`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      this.clearAuth();
      throw new Error('Session abgelaufen');
    }
    
    const user = await response.json();
    this.user = user;
    localStorage.setItem('olympus_user', JSON.stringify(user));
    return user;
  }

  async changePassword(altesPasswort, neuesPasswort) {
    const response = await fetch(`${SAME_ORIGIN_BASE}/api/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      },
      body: JSON.stringify({ altes_passwort: altesPasswort, neues_passwort: neuesPasswort })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Fehler beim Passwort ändern');
    }
    
    return data;
  }

  // ==================== Benutzer Verwaltung (Admin) ====================

  async listBenutzer(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.rolle) searchParams.append('rolle', params.rolle);
    if (params.aktiv !== undefined) searchParams.append('aktiv', params.aktiv);
    
    const url = `${BACKEND_URL}/api/benutzer${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await fetch(url, {
      headers: this.getAuthHeaders()
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.detail || 'Fehler beim Laden');
    }
    
    return data;
  }

  async getBenutzer(id) {
    const response = await fetch(`${BACKEND_URL}/api/benutzer/${id}`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) throw new Error('Benutzer nicht gefunden');
    return response.json();
  }

  async createBenutzer(data) {
    const response = await fetch(`${BACKEND_URL}/api/benutzer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      },
      body: JSON.stringify(data)
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.detail || 'Fehler beim Erstellen');
    }
    
    return result;
  }

  async updateBenutzer(id, data) {
    const response = await fetch(`${BACKEND_URL}/api/benutzer/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      },
      body: JSON.stringify(data)
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.detail || 'Fehler beim Aktualisieren');
    }
    
    return result;
  }

  async deleteBenutzer(id) {
    const response = await fetch(`${BACKEND_URL}/api/benutzer/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.detail || 'Fehler beim Löschen');
    }
    
    return result;
  }

  // Benutzer-Auswahl für Dropdowns
  async getBenutzerAuswahl() {
    const response = await fetch(`${BACKEND_URL}/api/benutzer/auswahl/alle`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) return [];
    return response.json();
  }

  // ==================== Benachrichtigungen ====================

  async listBenachrichtigungen(gelesen = null) {
    const params = gelesen !== null ? `?gelesen=${gelesen}` : '';
    const response = await fetch(`${BACKEND_URL}/api/benachrichtigungen${params}`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) return [];
    return response.json();
  }

  async getUngeleseneCount() {
    const response = await fetch(`${BACKEND_URL}/api/benachrichtigungen/ungelesen/count`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) return { count: 0 };
    return response.json();
  }

  async markAsRead(id) {
    const response = await fetch(`${BACKEND_URL}/api/benachrichtigungen/${id}/gelesen`, {
      method: 'PATCH',
      headers: this.getAuthHeaders()
    });
    
    return response.json();
  }

  async markAllAsRead() {
    const response = await fetch(`${BACKEND_URL}/api/benachrichtigungen/alle-gelesen`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    
    return response.json();
  }

  async deleteBenachrichtigung(id) {
    const response = await fetch(`${BACKEND_URL}/api/benachrichtigungen/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    
    return response.json();
  }
}

export const authAPI = new AuthAPI();
export default authAPI;
